/**
 * statsCalculator.js — SINGLE SOURCE OF TRUTH for all trade statistics.
 *
 * Every component, hook, and tab that needs win/loss, P&L, streaks,
 * or grouped performance stats MUST use these functions instead of
 * inline filter/reduce chains.
 *
 * Design principles:
 *   1. Pure functions — no React, no side effects, no state
 *   2. Composable — small building blocks that combine into complex stats
 *   3. Gross vs Net aware — optional `pnlAccessor` lets callers choose
 *   4. Defensive — handles undefined/NaN/empty gracefully
 *   5. Consistent — same answer everywhere, every time
 */

import { safePnl } from '../utils/formatters';
import { getTradeNetPnL } from '../config';

// ─────────────────────────────────────────────────────────────
// P&L ACCESSORS
// ─────────────────────────────────────────────────────────────

/**
 * Default P&L accessor — returns gross P&L from trade.pnl.
 * Use this when you don't need commission-adjusted values.
 */
export const grossPnlAccessor = (trade) => safePnl(trade);

/**
 * Create a net P&L accessor that subtracts commissions.
 * Requires an accounts array to look up broker/commission config.
 *
 * Usage:
 *   const getPnl = netPnlAccessor(accounts);
 *   const stats = calcTradeStats(trades, getPnl);
 *
 * @param {Array} accounts - Array of account objects
 * @returns {function} P&L accessor: (trade) => number
 */
export const netPnlAccessor = (accounts) => (trade) => {
  const account = accounts.find(a => a.id === trade.accountId);
  const { netPnl } = getTradeNetPnL(trade, account);
  return isNaN(netPnl) ? 0 : netPnl;
};


// ─────────────────────────────────────────────────────────────
// CORE TRADE STATS
// ─────────────────────────────────────────────────────────────

/**
 * Calculate comprehensive trade statistics from an array of trades.
 * This is the primary function — replaces 15+ independent implementations.
 *
 * @param {Array} trades - Array of trade objects
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Complete stats object
 */
export const calcTradeStats = (trades, getPnl = grossPnlAccessor) => {
  if (!trades || trades.length === 0) {
    return {
      totalTrades: 0,
      wins: 0,
      losses: 0,
      breakeven: 0,
      winRate: 0,
      totalPnL: 0,
      grossProfit: 0,
      grossLoss: 0,
      profitFactor: 0,
      avgPnL: 0,
      avgWin: 0,
      avgLoss: 0,
    };
  }

  let totalPnL = 0;
  let grossProfit = 0;
  let grossLoss = 0;
  let wins = 0;
  let losses = 0;
  let breakeven = 0;

  // Single pass through trades — O(n), not multiple filter/reduce chains
  for (const trade of trades) {
    const pnl = getPnl(trade);
    totalPnL += pnl;

    if (pnl > 0) {
      wins++;
      grossProfit += pnl;
    } else if (pnl < 0) {
      losses++;
      grossLoss += Math.abs(pnl);
    } else {
      breakeven++;
    }
  }

  const totalTrades = trades.length;
  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? Infinity : 0);
  const avgPnL = totalTrades > 0 ? totalPnL / totalTrades : 0;
  const avgWin = wins > 0 ? grossProfit / wins : 0;
  const avgLoss = losses > 0 ? grossLoss / losses : 0;

  return {
    totalTrades,
    wins,
    losses,
    breakeven,
    winRate,
    totalPnL,
    grossProfit,
    grossLoss,
    profitFactor,
    avgPnL,
    avgWin,
    avgLoss,
  };
};


// ─────────────────────────────────────────────────────────────
// R-MULTIPLE STATS
// ─────────────────────────────────────────────────────────────

/**
 * Calculate R-Multiple statistics from trades.
 * Only includes trades that have a valid, non-zero rMultiple.
 *
 * @param {Array} trades - Array of trade objects
 * @returns {object} R-stats
 */
export const calcRStats = (trades) => {
  if (!trades || trades.length === 0) {
    return { avgR: 0, totalR: 0, tradesWithR: 0 };
  }

  let totalR = 0;
  let count = 0;

  for (const trade of trades) {
    const r = parseFloat(trade.rMultiple);
    if (trade.rMultiple != null && trade.rMultiple !== '' && !isNaN(r) && isFinite(r)) {
      totalR += r;
      count++;
    }
  }

  return {
    avgR: count > 0 ? totalR / count : 0,
    totalR,
    tradesWithR: count,
  };
};


// ─────────────────────────────────────────────────────────────
// DAILY P&L + DAY-LEVEL STATS
// ─────────────────────────────────────────────────────────────

/**
 * Group trades by date and calculate daily P&L, green/red days,
 * best/worst day, average daily P&L, and trades per day.
 *
 * @param {Array} trades - Array of trade objects
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Daily stats
 */
export const calcDailyStats = (trades, getPnl = grossPnlAccessor) => {
  if (!trades || trades.length === 0) {
    return {
      dailyPnL: {},
      greenDays: 0,
      redDays: 0,
      flatDays: 0,
      totalDays: 0,
      bestDay: 0,
      worstDay: 0,
      avgDailyPnL: 0,
      tradesPerDay: 0,
    };
  }

  const dailyPnL = {};
  const dailyCounts = {};

  for (const trade of trades) {
    const date = trade.date;
    if (!date) continue;
    const pnl = getPnl(trade);
    dailyPnL[date] = (dailyPnL[date] || 0) + pnl;
    dailyCounts[date] = (dailyCounts[date] || 0) + 1;
  }

  const days = Object.keys(dailyPnL);
  const values = Object.values(dailyPnL);
  const totalDays = days.length;

  let greenDays = 0;
  let redDays = 0;
  let flatDays = 0;
  let bestDay = -Infinity;
  let worstDay = Infinity;

  for (const val of values) {
    if (val > 0) greenDays++;
    else if (val < 0) redDays++;
    else flatDays++;
    if (val > bestDay) bestDay = val;
    if (val < worstDay) worstDay = val;
  }

  if (totalDays === 0) {
    bestDay = 0;
    worstDay = 0;
  }

  const totalPnL = values.reduce((a, b) => a + b, 0);
  const avgDailyPnL = totalDays > 0 ? totalPnL / totalDays : 0;
  const tradesPerDay = totalDays > 0 ? trades.length / totalDays : 0;

  return {
    dailyPnL,
    greenDays,
    redDays,
    flatDays,
    totalDays,
    bestDay,
    worstDay,
    avgDailyPnL,
    tradesPerDay,
  };
};


// ─────────────────────────────────────────────────────────────
// STREAK ANALYSIS (BY DAY)
// ─────────────────────────────────────────────────────────────

/**
 * Calculate streaks based on consecutive GREEN/RED DAYS.
 * This is the canonical streak definition for the app:
 *   - A "win day" = total daily P&L > 0
 *   - A "loss day" = total daily P&L < 0
 *   - Flat days (P&L = 0) break the streak
 *
 * @param {Array} trades - Array of trade objects
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Streak data
 */
export const calcStreaks = (trades, getPnl = grossPnlAccessor) => {
  const { dailyPnL } = calcDailyStats(trades, getPnl);
  const sortedDays = Object.keys(dailyPnL).sort();

  if (sortedDays.length === 0) {
    return { currentStreak: 0, bestWinStreak: 0, worstLossStreak: 0 };
  }

  let bestWinStreak = 0;
  let worstLossStreak = 0;
  let tempWinStreak = 0;
  let tempLossStreak = 0;

  for (const day of sortedDays) {
    const pnl = dailyPnL[day];
    if (pnl > 0) {
      tempWinStreak++;
      tempLossStreak = 0;
      if (tempWinStreak > bestWinStreak) bestWinStreak = tempWinStreak;
    } else if (pnl < 0) {
      tempLossStreak++;
      tempWinStreak = 0;
      if (tempLossStreak > worstLossStreak) worstLossStreak = tempLossStreak;
    } else {
      // Flat day breaks both streaks
      tempWinStreak = 0;
      tempLossStreak = 0;
    }
  }

  // Current streak from most recent day
  const lastDay = sortedDays[sortedDays.length - 1];
  const lastPnL = dailyPnL[lastDay];
  let currentStreak = 0;
  if (lastPnL > 0) currentStreak = tempWinStreak;
  else if (lastPnL < 0) currentStreak = -tempLossStreak;

  return { currentStreak, bestWinStreak, worstLossStreak };
};


// ─────────────────────────────────────────────────────────────
// GROUPED STATS (by any key)
// ─────────────────────────────────────────────────────────────

/**
 * Group trades by a key function and calculate stats for each group.
 * Replaces dozens of per-symbol, per-ATM, per-setup, per-hour, etc. loops.
 *
 * @param {Array} trades - Array of trade objects
 * @param {function} keyFn - Function that returns the group key: (trade) => string
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Map of groupKey → { trades, ...stats, ...rStats }
 */
export const calcGroupedStats = (trades, keyFn, getPnl = grossPnlAccessor) => {
  if (!trades || trades.length === 0) return {};

  // Bucket trades into groups
  const groups = {};
  for (const trade of trades) {
    const key = keyFn(trade);
    if (key == null || key === '') continue;
    if (!groups[key]) groups[key] = [];
    groups[key].push(trade);
  }

  // Calculate stats for each group
  const result = {};
  for (const [key, groupTrades] of Object.entries(groups)) {
    result[key] = {
      trades: groupTrades,
      ...calcTradeStats(groupTrades, getPnl),
      ...calcRStats(groupTrades),
    };
  }

  return result;
};

/**
 * Convenience: group by trade property name.
 *
 * Usage:
 *   const bySymbol = calcStatsByProp(trades, 'symbol');
 *   const bySetup = calcStatsByProp(trades, 'setup');
 *   const byDirection = calcStatsByProp(trades, 'direction');
 *
 * @param {Array} trades - Array of trade objects
 * @param {string} prop - Property name on trade object
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Map of propValue → stats
 */
export const calcStatsByProp = (trades, prop, getPnl = grossPnlAccessor) =>
  calcGroupedStats(trades, (t) => t[prop] || 'Unknown', getPnl);


// ─────────────────────────────────────────────────────────────
// DIRECTION ANALYSIS
// ─────────────────────────────────────────────────────────────

/**
 * Calculate Long vs Short breakdown with best setup per direction.
 *
 * @param {Array} trades - Array of trade objects
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @param {number} [minTradesForBestSetup=20] - Min trades to qualify as "best setup"
 * @returns {object} { long: stats, short: stats }
 */
export const calcDirectionStats = (trades, getPnl = grossPnlAccessor, minTradesForBestSetup = 20) => {
  const longTrades = trades.filter(t => t.direction?.toLowerCase() === 'long');
  const shortTrades = trades.filter(t => t.direction?.toLowerCase() === 'short');

  const buildDirectionData = (dirTrades) => {
    const stats = calcTradeStats(dirTrades, getPnl);
    const rStats = calcRStats(dirTrades);

    // Best setup (by total P&L, min trade threshold)
    const setupStats = {};
    for (const t of dirTrades) {
      if (!t.setup) continue;
      if (!setupStats[t.setup]) setupStats[t.setup] = { pnl: 0, count: 0, wins: 0 };
      const pnl = getPnl(t);
      setupStats[t.setup].pnl += pnl;
      setupStats[t.setup].count++;
      if (pnl > 0) setupStats[t.setup].wins++;
    }

    const bestSetup = Object.entries(setupStats)
      .filter(([_, s]) => s.count >= minTradesForBestSetup)
      .sort((a, b) => b[1].pnl - a[1].pnl)[0] || null;

    return { ...stats, ...rStats, bestSetup };
  };

  return {
    long: buildDirectionData(longTrades),
    short: buildDirectionData(shortTrades),
  };
};


// ─────────────────────────────────────────────────────────────
// FULL OVERVIEW (combines everything for the Overview tab)
// ─────────────────────────────────────────────────────────────

/**
 * Calculate all Overview tab stats in a single call.
 * Replaces 6+ independent IIFE blocks in OverviewTab.js.
 *
 * @param {Array} trades - closedFilteredTrades
 * @param {function} [getPnl=grossPnlAccessor] - P&L accessor function
 * @returns {object} Complete overview data
 */
export const calcOverviewStats = (trades, getPnl = grossPnlAccessor) => {
  const core = calcTradeStats(trades, getPnl);
  const rStats = calcRStats(trades);
  const daily = calcDailyStats(trades, getPnl);
  const streaks = calcStreaks(trades, getPnl);
  const direction = calcDirectionStats(trades, getPnl);

  return {
    ...core,
    ...rStats,
    ...daily,
    ...streaks,
    ...direction,
  };
};


// ─────────────────────────────────────────────────────────────
// SESSION / CAMPAIGN STATS
// ─────────────────────────────────────────────────────────────

/**
 * Calculate stats for each trade session (campaign/thesis group).
 * @param {Array} trades - All trades to analyze
 * @param {Array} sessions - Session objects [{id, name, thesis, date, color}]
 * @param {Function} getPnl - P&L accessor (gross or net)
 * @returns {Object} Map of sessionId → { session, trades, ...stats }
 */
export const calcSessionStats = (trades, sessions, getPnl = grossPnlAccessor) => {
  const result = {};
  for (const session of sessions) {
    const sessionTrades = trades.filter(t => t.sessionId === session.id);
    if (sessionTrades.length === 0) continue;
    result[session.id] = {
      session,
      trades: sessionTrades,
      ...calcTradeStats(sessionTrades, getPnl),
      ...calcRStats(sessionTrades),
    };
  }
  return result;
};
